import EditItemDialog from 'components/util/EditItemDialog';
import { Form, FormikProvider, useFormik } from 'formik';
import { FormControl, InputLabel, Select, MenuItem, FormHelperText } from '@mui/material';
import { Sender } from '../../../@types/organization';
import * as Yup from 'yup';
import { addSenders, updateSender } from 'redux/slices/organization';
import useCustomDispatch from 'redux/dispatch';
import { useRegisterUnsavedFormChanges } from 'components/form/useRegisterUnsavedFormChanges';
import { useTranslator } from 'translation/useTranslator';
import { TextField } from '@nps.today/component-library';

const componentDataTestId = 'add-sender-dialog';

const SENDER_TYPES = ['SMS', 'Email'];

type AddSenderDialogProps = {
  display: boolean;
  close: () => void;
  fetchSenders: () => void;
  senderToEdit?: Sender;
};
const AddSenderDialog = ({ display, close, senderToEdit, fetchSenders }: AddSenderDialogProps) => {
  const translator = useTranslator();
  const customDispatch = useCustomDispatch();

  const smsSenderRegEx = /^(?=.*[a-zA-Z0-9])(?=.*[a-zA-Z0-9])([a-zA-Z0-9 .]{3,11})$/gm;
  const smsSenderNotContainingDotOrSpaceRegEx = /^((?![\s|.]).)*$/;
  const UpdateUserSchema = Yup.object().shape({
    senderType: Yup.string().required(translator.typeRequired()),
    sender: Yup.string()
      .when('senderType', {
        is: 'SMS',
        then: Yup.string().matches(smsSenderRegEx, translator.smsSenderIsNotValid())
      })
      .required(translator.senderRequired())
  });

  const formik = useFormik<Sender>({
    enableReinitialize: true,
    initialValues: {
      sender: senderToEdit?.sender ?? '',
      senderType: senderToEdit?.senderType ?? ''
    },
    validationSchema: UpdateUserSchema,
    onSubmit: async (values, { setSubmitting }) => {
      const onSuccess = () => {
        //Update senders
        fetchSenders();

        //close dialog
        onClose();
      };
      const onFinally = () => {
        setSubmitting(false);
      };

      setSubmitting(true);
      if (senderToEdit) {
        customDispatch({
          action: updateSender,
          actionParameters: { ...senderToEdit, ...values },
          onSuccess: onSuccess,
          onFinally: onFinally
        });
      } else {
        const newSenders = [values];
        customDispatch({
          action: addSenders,
          actionParameters: newSenders,
          onSuccess: onSuccess,
          onFinally: onFinally
        });
      }
    }
  });

  const {
    submitForm,
    resetForm,
    errors,
    touched,
    dirty,
    isSubmitting,
    handleSubmit,
    getFieldProps,
    values,
    setFieldValue
  } = formik;

  useRegisterUnsavedFormChanges(dirty);

  const onClose = () => {
    close();
    resetForm();
  };

  const isDotOrWhitespaceUsedInSmsSender =
    values.senderType === 'SMS' && !smsSenderNotContainingDotOrSpaceRegEx.test(values.sender);

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" onSubmit={handleSubmit}>
        <EditItemDialog
          itemType="sender"
          editable
          isOpen={display}
          title={senderToEdit ? translator.editSender() : translator.addNewSender()}
          editContent={[
            <TextField
              dataTestId={`${componentDataTestId}-sender`}
              key="sender"
              fullWidth
              label={translator.sender()}
              {...getFieldProps('sender')}
              required
              error={Boolean(touched.sender && errors.sender)}
              helperText={
                (touched.sender && errors.sender) ||
                (isDotOrWhitespaceUsedInSmsSender &&
                  translator.dotAndWhiteSpaceMayNotBeAcceptedInSomeCountries())
              }
            />,
            <FormControl fullWidth required key="senderType">
              <InputLabel id="senderType">{translator.type()}</InputLabel>
              <Select
                fullWidth
                value={values.senderType}
                onChange={(event: any) => {
                  setFieldValue('senderType', event.target.value);
                }}
                labelId="senderType"
                label={translator.type()}
                error={Boolean(touched.senderType && errors.senderType)}
              >
                {SENDER_TYPES?.map((type: any, index: number) => (
                  <MenuItem key={index} value={type} disabled={type === 'Email'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                      {type === 'Email' ? translator.emailSenderTypeNotYetImplemented() : type}
                    </div>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText error={Boolean(touched.senderType && errors.senderType)}>
                {touched.senderType && errors.senderType}
              </FormHelperText>
            </FormControl>
          ]}
          dirty={dirty}
          isSubmitting={isSubmitting}
          close={() => onClose()}
          resetForm={resetForm}
          submitForm={submitForm}
        />
      </Form>
    </FormikProvider>
  );
};

export default AddSenderDialog;
